import React, { useMemo, forwardRef, ForwardRefRenderFunction } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormikProps } from "formik";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CountryEnums, StyleEnums } from "models/enums";
import { FormFieldConstants, SalaryDeviationConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { SalaryDeviationAllowanceFormContainerProps } from "models/propTypes/salaryDeviationPropTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import CreateAllowanceForm from "./allowanceForm";
import { useFloatingMessages, useMountedState, LoadingWithLogo, StaticInfoMessage } from "components/common";
import { useSalaryDeviationsService } from "serviceHooks";
import useDeviationPermissions from "../usePermissions";
import useIsReadOnly from "./useIsReadOnly";
//#endregion
import { DateHelper, LocalizationHelperInstance, RegularExpressions, UtilsHelper, WorkerHelper } from "utils/helpers";
import * as Yup from "yup";
import cssExports from "theme/styles/main.module.scss";

const CreateAllowanceFormContainer: ForwardRefRenderFunction<
  FormHandles,
  SalaryDeviationAllowanceFormContainerProps
> = (props, ref) => {
  const salaryDeviationsService = useSalaryDeviationsService();
  const { salaryDeviationId, deviationTypes, monthState, yearMonth } = props;
  const { getMounted } = useMountedState();
  const [loading, setLoading] = React.useState(false);

  const { isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } = useDeviationPermissions();
  const [deviationType, setDeviationType] = React.useState<WintSuperkollAPIModel.SalaryDeviationType>();
  const currentEmployee = useSelector((state: AppState) => state.auth.loggedInUser);
  const allowedDeviationTypes = useSelector((state: AppState) => state.auth.selectedCompany.allowedDeviationTypes);

  const enabledAllowanceTypes = (deviationTypes ?? SalaryDeviationConstants.AllowanceTypes).filter(
    (value) => allowedDeviationTypes?.includes(value) || deviationType === value
  );

  const [initialValues, setInitialValues] = React.useState<SalaryDeviationFormTypes.CreateAllowance>({
    deviationType: enabledAllowanceTypes[0],
    personId: props.personId ?? currentEmployee.personId,
    personName: props.personName ?? currentEmployee.name,
    yearAndMonth: yearMonth ? DateHelper.getDateFromYearMonth(yearMonth) : new Date(),
    fromTime: "08:00",
    toTime: "17:00",
    workHours: 8,
    workMinutes: 0,
    hasConnectedTimeReports: false,
  });

  const { enqueueMessage } = useFloatingMessages();

  const loadSalaryDeviation = (id: number) => {
    props.onFormValidationChange && props.onFormValidationChange(false);
    setLoading(true);
    salaryDeviationsService
      .get(id)
      .then((salaryDeviation) => {
        if (getMounted()) {
          const deviationYearMonth = DateHelper.getDateFromYearMonth(salaryDeviation.yearAndMonth);
          const validStartDay = DateHelper.getFirstDayOfMonth(deviationYearMonth);
          const validEndDay = DateHelper.getFirstDayOfNextMonth(deviationYearMonth);
          setDeviationType(salaryDeviation.deviationType);
          setInitialValues({
            ...salaryDeviation,
            customName: salaryDeviation.customDeviationName,
            yearAndMonth: deviationYearMonth,
            fromTime: salaryDeviation.fromDate ? DateHelper.getHoursAndMinutes(salaryDeviation.fromDate) : undefined,
            toTime: salaryDeviation.toDate ? DateHelper.getHoursAndMinutes(salaryDeviation.toDate) : undefined,
            mealDeductions: salaryDeviation.mealDeductions?.map((item) => ({
              id: UtilsHelper.getUniqueId(),
              day: item.day,
              breakfast: item.breakfast,
              lunch: item.lunch,
              dinner: item.dinner,
            })),
            sumDescriptions: getSumDescriptions(salaryDeviation),
            mileage: salaryDeviation.distance,
            vehicleType: salaryDeviation.vehicleType,
            vehicleTypeName:
              salaryDeviation.vehicleType !== undefined
                ? LocalizationHelperInstance.getFormattedMessage(
                    `SalaryDeviation.CarType_${salaryDeviation.vehicleType}`
                  )
                : undefined,
            countryCode: salaryDeviation.country,
            countryName: salaryDeviation.country
              ? LocalizationHelperInstance.getFormattedMessage(salaryDeviation?.country)
              : undefined,
            workHours: Math.trunc(salaryDeviation.hours ?? 8),
            workMinutes: salaryDeviation.hours
              ? (salaryDeviation.hours - Math.trunc(salaryDeviation.hours ?? 0)) * 60
              : 0,
            restrictDateRange: { start: validStartDay, end: validEndDay },
            hasConnectedTimeReports: salaryDeviation.hasConnectedTimeReports,
            isAdminOnlyType: salaryDeviation.isAdminOnlyType,
          });
        }
      })
      .then(() => {
        setLoading(false);
        props.onFormValidationChange && props.onFormValidationChange(true);
      });
  };

  React.useEffect(() => {
    if (salaryDeviationId) {
      loadSalaryDeviation(salaryDeviationId);
    }
  }, [salaryDeviationId]);

  const getSumDescriptions = (salaryDeviation: WintSuperkollAPIModel.SalaryDeviation) => {
    let sumDescriptions;
    switch (salaryDeviation?.deviationType) {
      case WintSuperkollAPIModel.SalaryDeviationType.Bonus:
        sumDescriptions = salaryDeviation.bonus;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionFee:
        sumDescriptions = salaryDeviation.provisions;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationPaid:
        sumDescriptions = salaryDeviation.commissionsVacationPaid;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationSaved:
        sumDescriptions = salaryDeviation.commissionsVacationSaved;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.WithdrawalsSavedVacation:
        sumDescriptions = salaryDeviation.commissionWithdrawalsSavedVacation;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.CustomDeviationCompensation:
        sumDescriptions = salaryDeviation.customDeviationCompensation;
        break;
      default:
        return [];
    }

    return sumDescriptions?.map(
      (x): SalaryDeviationFormTypes.SumDescription => ({ ...x, id: UtilsHelper.getUniqueId() })
    );
  };

  const setSumDescriptions = (
    sumDescriptions: WintSuperkollAPIModel.SumDescriptionDto[],
    salaryDeviation: WintSuperkollAPIModel.SalaryDeviationPost
  ) => {
    switch (salaryDeviation?.deviationType) {
      case WintSuperkollAPIModel.SalaryDeviationType.Bonus:
        salaryDeviation.bonus = sumDescriptions;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionFee:
        salaryDeviation.provisions = sumDescriptions;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationPaid:
        salaryDeviation.commissionsVacationPaid = sumDescriptions;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationSaved:
        salaryDeviation.commissionsVacationSaved = sumDescriptions;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.WithdrawalsSavedVacation:
        salaryDeviation.commissionWithdrawalsSavedVacation = sumDescriptions;
        break;
      case WintSuperkollAPIModel.SalaryDeviationType.CustomDeviationCompensation:
        salaryDeviation.customDeviationCompensation = sumDescriptions;
        break;
    }
  };

  const validationSchema = useMemo(
    () =>
      Yup.object<SalaryDeviationFormTypes.CreateAllowance>().shape(
        {
          personId: Yup.number().required(TranslationKeys.Validations.Required),
          personName: Yup.string().required(TranslationKeys.Validations.Required),
          customName: Yup.string().when(
            FormFieldConstants.SalaryDeviation.CreateLeave.DeviationType,
            ([type], schema: Yup.StringSchema) =>
              (type === WintSuperkollAPIModel.SalaryDeviationType.CustomDeviationCompensation &&
                schema.required(TranslationKeys.Validations.Required)) ||
              schema
          ),
          yearAndMonth: Yup.date()
            .typeError(TranslationKeys.Validations.InvalidDate)
            .when(
              FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
              ([value], schema: Yup.DateSchema) =>
                (![
                  WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                  WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
                ].some((t) => t === value) &&
                  schema.required(TranslationKeys.Validations.Required)) ||
                schema
            ),
          deviationType: Yup.mixed()
            .oneOf(Object.values(WintSuperkollAPIModel.SalaryDeviationType))
            .required(TranslationKeys.Validations.Required),
          fromDate: Yup.date()
            .typeError(TranslationKeys.Validations.InvalidDate)
            .when(
              FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
              ([value], schema: Yup.DateSchema) =>
                ([
                  WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                  WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
                ].some((t) => t === value) &&
                  schema.required(TranslationKeys.Validations.Required)) ||
                schema
            )
            .when(
              FormFieldConstants.SalaryDeviation.CreateLeave.RestrictDateRange,
              ([dateRange], schema: Yup.DateSchema) =>
                (dateRange?.start &&
                  dateRange?.end &&
                  schema
                    .min(
                      dateRange.start,
                      TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth
                    )
                    .max(
                      dateRange.end,
                      TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth
                    )) ||
                schema
            ),
          toDate: Yup.date()
            .typeError(TranslationKeys.Validations.InvalidDate)
            .when(
              FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
              ([value], schema: Yup.DateSchema) =>
                ([
                  WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                  WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
                ].some((t) => t === value) &&
                  schema
                    .required(TranslationKeys.Validations.Required)
                    .min(Yup.ref(FormFieldConstants.SalaryDeviation.CreateAllowance.FromDate))) ||
                schema
            )
            .when(
              FormFieldConstants.SalaryDeviation.CreateLeave.RestrictDateRange,
              ([dateRange], schema: Yup.DateSchema) =>
                (dateRange?.start &&
                  dateRange?.end &&
                  schema
                    .min(
                      dateRange.start,
                      TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth
                    )
                    .max(
                      dateRange.end,
                      TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth
                    )) ||
                schema
            ),
          fromTime: Yup.string()
            .matches(RegularExpressions.Time, {
              message: TranslationKeys.Validations.InvalidTime,
              excludeEmptyString: true,
            })
            .when(
              FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
              ([value], schema: Yup.StringSchema) =>
                ([
                  WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                  WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
                ].some((t) => t === value) &&
                  schema.required(TranslationKeys.Validations.Required)) ||
                schema
            ),
          toTime: Yup.string()
            .matches(RegularExpressions.Time, {
              message: TranslationKeys.Validations.InvalidTime,
              excludeEmptyString: true,
            })
            .when(
              FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
              ([value], schema: Yup.StringSchema) =>
                ([
                  WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                  WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
                ].some((t) => t === value) &&
                  schema.required(TranslationKeys.Validations.Required)) ||
                schema
            ),
          mealDeductions: Yup.array().of(
            Yup.object<SalaryDeviationFormTypes.SalaryDeviationMeal>({
              id: Yup.number(),
              day: Yup.date()
                .typeError(TranslationKeys.Validations.InvalidDate)
                .required(TranslationKeys.Validations.Required),
              breakfast: Yup.boolean(),
              lunch: Yup.boolean(),
              dinner: Yup.boolean(),
            })
          ),
          sumDescriptions: Yup.array()
            .of(
              Yup.object<SalaryDeviationFormTypes.SumDescription>({
                id: Yup.number(),
                sum: Yup.number().required(TranslationKeys.Validations.Required),
                description: Yup.string(),
              })
            )
            .when(
              FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
              (
                [value]: [WintSuperkollAPIModel.SalaryDeviationType],
                schema: ArraySchema<SalaryDeviationFormTypes.SumDescription>
              ) =>
                ([
                  WintSuperkollAPIModel.SalaryDeviationType.Bonus,
                  WintSuperkollAPIModel.SalaryDeviationType.CommissionFee,
                  WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationPaid,
                  WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationSaved,
                  WintSuperkollAPIModel.SalaryDeviationType.WithdrawalsSavedVacation,
                  WintSuperkollAPIModel.SalaryDeviationType.CustomDeviationCompensation,
                ].some((t) => t === value) &&
                  schema.required(TranslationKeys.Validations.AtLeastOneItem)) ||
                schema
            ),
          mileage: Yup.number().when(
            FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
            ([value], schema: Yup.NumberSchema) =>
              (value === WintSuperkollAPIModel.SalaryDeviationType.Mileage &&
                schema
                  .required(TranslationKeys.Validations.Required)
                  .min(0.01, TranslationKeys.Validations.Required)) ||
              schema
          ),
          vehicleType: Yup.number().when(
            FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
            ([value], schema: Yup.NumberSchema) =>
              (value === WintSuperkollAPIModel.SalaryDeviationType.Mileage &&
                schema.required(TranslationKeys.Validations.Required)) ||
              schema
          ),
          vehicleTypeName: Yup.string().when(
            FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
            ([value], schema: Yup.StringSchema) =>
              (value === WintSuperkollAPIModel.SalaryDeviationType.Mileage &&
                schema.required(TranslationKeys.Validations.Required)) ||
              schema
          ),
          countryCode: Yup.string(),
          countryName: Yup.string().when(
            FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
            ([value], schema: Yup.StringSchema) =>
              (value === WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign &&
                schema.required(TranslationKeys.Validations.Required)) ||
              schema
          ),
          workHours: Yup.number().when(
            FormFieldConstants.SalaryDeviation.CreateAllowance.DeviationType,
            ([value], schema: Yup.NumberSchema) =>
              ([
                WintSuperkollAPIModel.SalaryDeviationType.WithdrawalCompensation,
                WintSuperkollAPIModel.SalaryDeviationType.DepositedCompensation,
                WintSuperkollAPIModel.SalaryDeviationType.WorkedHours,
              ].some((t) => t === value) &&
                schema.when(
                  FormFieldConstants.SalaryDeviation.CreateAllowance.WorkMinutes,
                  ([minutes], schema: Yup.NumberSchema) =>
                    ((!minutes || minutes < 0) &&
                      schema
                        .required(TranslationKeys.Validations.Required)
                        .min(1, TranslationKeys.Validations.InvalidTime)) ||
                    schema
                )) ||
              schema
          ),
          workMinutes: Yup.number().when(
            FormFieldConstants.SalaryDeviation.CreateAllowance.WorkHours,
            ([hours], schema: Yup.NumberSchema) =>
              (hours >= 0 &&
                schema
                  .min(0, TranslationKeys.Validations.InvalidMinutesRange)
                  .max(59, TranslationKeys.Validations.InvalidMinutesRange)) ||
              schema
          ),
          comment: Yup.string(),
          restrictDateRange: Yup.object(),
          hasConnectedTimeReports: Yup.boolean(),
          isAdminOnlyType: Yup.boolean(),
        },
        [
          [
            FormFieldConstants.SalaryDeviation.CreateAllowance.WorkHours,
            FormFieldConstants.SalaryDeviation.CreateAllowance.WorkMinutes,
          ],
        ]
      ),
    []
  );

  const handleFormSubmit = async (values: SalaryDeviationFormTypes.CreateAllowance) => {
    const deviation: WintSuperkollAPIModel.SalaryDeviationPost = {
      personId: values.personId,
      customDeviationName: values.customName,
      yearAndMonth: DateHelper.getYearMonth(values.yearAndMonth),
      deviationType:
        values.deviationType === WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign
          ? WintSuperkollAPIModel.SalaryDeviationType.Allowance
          : values.deviationType,
      fromDate: DateHelper.resetTime(values.fromDate),
      toDate: DateHelper.resetTime(values.toDate),
      fromTime: values.fromTime,
      toTime: values.toTime,
      distance: values.mileage,
      vehicleType: values.vehicleType,
      mealDeductions: values.mealDeductions?.map(
        (item): WintSuperkollAPIModel.SalaryDeviationDeviationMeal => ({
          day: item.day,
          breakfast: item.breakfast,
          lunch: item.lunch,
          dinner: item.dinner,
        })
      ),
      comment: values.comment,
      country:
        values.deviationType === WintSuperkollAPIModel.SalaryDeviationType.Allowance
          ? CountryEnums.CountryCode.SE
          : values.countryCode,
      hours: +(values.workHours ?? 0) + +(values.workMinutes ?? 0) / 60,
    };

    setSumDescriptions((values.sumDescriptions ?? []) as WintSuperkollAPIModel.SumDescriptionDto[], deviation);

    const result = salaryDeviationId
      ? await salaryDeviationsService.update({ ...deviation, id: salaryDeviationId })
      : await salaryDeviationsService.create(deviation);

    if (result.data.errorMessage && result.data.errorMessage?.length > 0) {
      enqueueMessage(result.data.errorMessage, StyleEnums.DisplayType.Danger);
      throw new Error(result.data.errorMessage);
    }
  };

  const mealDatesWorkerInstance = React.useMemo(() => WorkerHelper.getSalaryDeviationMealDatesWorker(), []);

  const onDatesChange = (
    name: string,
    value: any,
    formikProps: FormikProps<SalaryDeviationFormTypes.CreateAllowance>
  ) => {
    const { setFieldValue, setFieldTouched, values } = formikProps;
    const startDate = values.fromDate;
    const endDate = values.toDate;

    if (!startDate || !endDate) return;

    mealDatesWorkerInstance.onmessage = (e: MessageEvent<SalaryDeviationFormTypes.SalaryDeviationMeal[]>) => {
      setFieldValue(FormFieldConstants.SalaryDeviation.CreateAllowance.MealDeductions, e.data);
      setFieldTouched(FormFieldConstants.SalaryDeviation.CreateAllowance.MealDeductions);
    };

    mealDatesWorkerInstance.postMessage({ startDate, endDate, existingDayHours: values.mealDeductions });
  };

  const { getIsReadOnly } = useIsReadOnly();
  const isReadOnly = getIsReadOnly({
    hasConnectedTimeReports: initialValues.hasConnectedTimeReports,
    isAdminOnlyType: initialValues.isAdminOnlyType ?? false,
    isMonthDisabled: salaryDeviationsService.isMonthDisabled(monthState),
  });

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      {initialValues.hasConnectedTimeReports && (
        <StaticInfoMessage gutterBottom>
          {LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.ConnectedToProjectTimeInfoMessage
          )}
        </StaticInfoMessage>
      )}
      <CreateAllowanceForm
        ref={ref}
        defaultValues={initialValues}
        validationSchema={validationSchema}
        handleSubmit={handleFormSubmit}
        onFormValidationChange={props.onFormValidationChange}
        isSalaryAdmin={isSalaryAdmin}
        isBusinessUnitManagerWithSalaryRights={isBusinessUnitManagerWithSalaryRights}
        isDeviationAdmin={isDeviationAdmin}
        datesChangeHandler={onDatesChange}
        allowedDeviationTypes={enabledAllowanceTypes}
        disabled={isReadOnly}
        isNew={salaryDeviationId ? false : true}
      />
    </>
  );
};

export default forwardRef(CreateAllowanceFormContainer);
